import React, { useState, useEffect } from 'react';
import { Brain, Plus, Save, Trash2, Globe, MessageSquare, BookOpen, Sparkles, Check, X, Edit2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AITeacher } from './AITeacher';

interface KnowledgeItem {
  id?: number;
  category: string;
  question: string;
  answer: string;
  language: string;
  usage_count?: number;
}

interface IntentResponse {
  id?: number;
  intent: string;
  language: string;
  response_template: string;
  requires_context?: boolean;
}

export const AITrainer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'knowledge' | 'intents' | 'analytics'>('knowledge');
  const [language, setLanguage] = useState<'en' | 'pt'>('en');
  const [knowledgeItems, setKnowledgeItems] = useState<KnowledgeItem[]>([]);
  const [intentResponses, setIntentResponses] = useState<IntentResponse[]>([]);
  const [editingItem, setEditingItem] = useState<KnowledgeItem | IntentResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  
  // Form states
  const [newKnowledge, setNewKnowledge] = useState<KnowledgeItem>({
    category: '',
    question: '',
    answer: '',
    language: 'en'
  });
  
  const [newIntent, setNewIntent] = useState<IntentResponse>({
    intent: '',
    language: 'en',
    response_template: '',
    requires_context: false
  });

  const categories = {
    en: ['General', 'Pricing', 'Technical', 'Product', 'Support', 'Sales'],
    pt: ['Geral', 'Preços', 'Técnico', 'Produto', 'Suporte', 'Vendas']
  };

  const intents = [
    'greeting', 'pricing_inquiry', 'appointment_booking', 
    'technical_support', 'product_information', 'complaint', 
    'thanks', 'general_question'
  ];

  // Load existing data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Fetch knowledge base
      const knowledgeResponse = await fetch('https://biig-api.b-9f2.workers.dev/api/training/knowledge');
      const knowledgeData = await knowledgeResponse.json();
      
      // Fetch intent responses
      const intentsResponse = await fetch('https://biig-api.b-9f2.workers.dev/api/training/intents');
      const intentsData = await intentsResponse.json();
      
      if (knowledgeData.items) {
        setKnowledgeItems(knowledgeData.items);
      } else {
        // Set default data if empty
        setKnowledgeItems([
          {
            id: 1,
            category: 'Product',
            question: 'What is WhatsApp Business automation?',
            answer: 'WhatsApp Business automation allows you to automatically respond to customer messages, manage conversations at scale, and integrate with your existing CRM and business tools.',
            language: 'en',
            usage_count: 0
          }
        ]);
      }
      
      if (intentsData.items) {
        setIntentResponses(intentsData.items);
      } else {
        // Set default data if empty
        setIntentResponses([
          {
            id: 1,
            intent: 'greeting',
            language: 'en',
            response_template: 'Hello! Welcome to biig.dev. How can I help you today?',
            requires_context: false
          }
        ]);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      // Use fallback data if API fails
      setKnowledgeItems([]);
      setIntentResponses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveKnowledge = async () => {
    setSaveStatus('saving');
    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/training/knowledge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newKnowledge)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Add to local state with the returned ID
        setKnowledgeItems([...knowledgeItems, { ...newKnowledge, id: data.id || Date.now() }]);
        setNewKnowledge({ category: '', question: '', answer: '', language: language });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
        
        // Reload data to get latest from server
        loadData();
      } else {
        throw new Error(data.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Failed to save:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const saveIntent = async () => {
    setSaveStatus('saving');
    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/training/intents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newIntent)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Add to local state with the returned ID
        setIntentResponses([...intentResponses, { ...newIntent, id: data.id || Date.now() }]);
        setNewIntent({ intent: '', language: language, response_template: '', requires_context: false });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
        
        // Reload data to get latest from server
        loadData();
      } else {
        throw new Error(data.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Failed to save:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const deleteItem = (id: number, type: 'knowledge' | 'intent') => {
    if (type === 'knowledge') {
      setKnowledgeItems(knowledgeItems.filter(item => item.id !== id));
    } else {
      setIntentResponses(intentResponses.filter(item => item.id !== id));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl">
                <Brain className="text-white" size={28} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  AI Training Center
                  <span className="px-2 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-xs font-semibold flex items-center gap-1">
                    <Sparkles size={12} />
                    CloudFlare AI
                  </span>
                </h1>
                <p className="text-gray-500">Teach your AI to be smarter</p>
              </div>
            </div>
            
            {/* Language Toggle */}
            <button
              onClick={() => setLanguage(language === 'en' ? 'pt' : 'en')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Globe size={16} />
              <span className="font-medium">{language === 'en' ? 'English' : 'Português'}</span>
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-6">
            <button
              onClick={() => setActiveTab('knowledge')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'knowledge'
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BookOpen size={16} className="inline mr-2" />
              Knowledge Base
            </button>
            <button
              onClick={() => setActiveTab('intents')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'intents'
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <MessageSquare size={16} className="inline mr-2" />
              Intent Responses
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'analytics'
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Sparkles size={16} className="inline mr-2" />
              Analytics
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Plus size={20} />
                {activeTab === 'knowledge' ? 'Add Knowledge' : 'Add Intent Response'}
              </h2>
              
              {activeTab === 'knowledge' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={newKnowledge.category}
                      onChange={(e) => setNewKnowledge({ ...newKnowledge, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select category</option>
                      {categories[language].map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Question
                    </label>
                    <input
                      type="text"
                      value={newKnowledge.question}
                      onChange={(e) => setNewKnowledge({ ...newKnowledge, question: e.target.value })}
                      placeholder={language === 'en' ? 'What question should this answer?' : 'Qual pergunta isso deve responder?'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Answer
                    </label>
                    <textarea
                      value={newKnowledge.answer}
                      onChange={(e) => setNewKnowledge({ ...newKnowledge, answer: e.target.value, language })}
                      placeholder={language === 'en' ? 'Provide a helpful answer...' : 'Forneça uma resposta útil...'}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <button
                    onClick={saveKnowledge}
                    disabled={!newKnowledge.category || !newKnowledge.question || !newKnowledge.answer}
                    className="w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {saveStatus === 'saving' ? (
                      <>Saving...</>
                    ) : saveStatus === 'saved' ? (
                      <><Check size={16} /> Saved!</>
                    ) : (
                      <><Save size={16} /> Save to Knowledge Base</>
                    )}
                  </button>
                </div>
              ) : activeTab === 'intents' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Intent Type
                    </label>
                    <select
                      value={newIntent.intent}
                      onChange={(e) => setNewIntent({ ...newIntent, intent: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select intent</option>
                      {intents.map(intent => (
                        <option key={intent} value={intent}>
                          {intent.replace(/_/g, ' ').charAt(0).toUpperCase() + intent.replace(/_/g, ' ').slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Response Template
                    </label>
                    <textarea
                      value={newIntent.response_template}
                      onChange={(e) => setNewIntent({ ...newIntent, response_template: e.target.value, language })}
                      placeholder={language === 'en' ? 'Enter the response template...' : 'Digite o modelo de resposta...'}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="requires-context"
                      checked={newIntent.requires_context}
                      onChange={(e) => setNewIntent({ ...newIntent, requires_context: e.target.checked })}
                      className="rounded text-purple-500 focus:ring-purple-500"
                    />
                    <label htmlFor="requires-context" className="text-sm text-gray-700">
                      {language === 'en' ? 'Requires context from conversation' : 'Requer contexto da conversa'}
                    </label>
                  </div>
                  
                  <button
                    onClick={saveIntent}
                    disabled={!newIntent.intent || !newIntent.response_template}
                    className="w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {saveStatus === 'saving' ? (
                      <>Saving...</>
                    ) : saveStatus === 'saved' ? (
                      <><Check size={16} /> Saved!</>
                    ) : (
                      <><Save size={16} /> Save Intent Response</>
                    )}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center py-8">
                    <Sparkles className="mx-auto text-purple-500 mb-4" size={48} />
                    <h3 className="text-lg font-semibold mb-2">AI Performance</h3>
                    <p className="text-gray-500 text-sm">
                      Track how well your AI is performing
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Accuracy</span>
                        <span className="font-bold text-purple-600">94.5%</span>
                      </div>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full" style={{ width: '94.5%' }}></div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Response Time</span>
                        <span className="font-bold text-green-600">1.2s</span>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Total Trained Items</span>
                        <span className="font-bold text-blue-600">{knowledgeItems.length + intentResponses.length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* List Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-lg font-bold mb-4">
                {activeTab === 'knowledge' ? 'Knowledge Base' : activeTab === 'intents' ? 'Intent Responses' : 'Training Analytics'}
              </h2>
              
              {isLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                  <p className="mt-2 text-gray-500">Loading...</p>
                </div>
              ) : activeTab === 'knowledge' ? (
                <div className="space-y-3">
                  {knowledgeItems
                    .filter(item => item.language === language)
                    .map(item => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                                {item.category}
                              </span>
                              <span className="text-xs text-gray-500">
                                Used {item.usage_count || 0} times
                              </span>
                            </div>
                            <p className="font-medium text-gray-900 mb-1">{item.question}</p>
                            <p className="text-sm text-gray-600">{item.answer}</p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => setEditingItem(item)}
                              className="p-1 text-gray-400 hover:text-purple-500"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => deleteItem(item.id!, 'knowledge')}
                              className="p-1 text-gray-400 hover:text-red-500"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  
                  {knowledgeItems.filter(item => item.language === language).length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <BookOpen className="mx-auto mb-4 text-gray-300" size={48} />
                      <p>{language === 'en' ? 'No knowledge items yet. Add some above!' : 'Ainda não há itens de conhecimento. Adicione alguns acima!'}</p>
                    </div>
                  )}
                </div>
              ) : activeTab === 'intents' ? (
                <div className="space-y-3">
                  {intentResponses
                    .filter(item => item.language === language)
                    .map(item => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                {item.intent.replace(/_/g, ' ')}
                              </span>
                              {item.requires_context && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                                  Contextual
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{item.response_template}</p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => setEditingItem(item)}
                              className="p-1 text-gray-400 hover:text-purple-500"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => deleteItem(item.id!, 'intent')}
                              className="p-1 text-gray-400 hover:text-red-500"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  
                  {intentResponses.filter(item => item.language === language).length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <MessageSquare className="mx-auto mb-4 text-gray-300" size={48} />
                      <p>{language === 'en' ? 'No intent responses yet. Add some above!' : 'Ainda não há respostas de intenção. Adicione algumas acima!'}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h3 className="font-semibold text-purple-900 mb-2">Most Used Knowledge</h3>
                    <div className="space-y-2">
                      {knowledgeItems
                        .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))
                        .slice(0, 3)
                        .map(item => (
                          <div key={item.id} className="flex justify-between text-sm">
                            <span className="text-gray-700 truncate">{item.question}</span>
                            <span className="text-purple-600 font-medium">{item.usage_count}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-2">Language Distribution</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">English</span>
                        <span className="text-green-600 font-medium">
                          {knowledgeItems.filter(i => i.language === 'en').length + intentResponses.filter(i => i.language === 'en').length}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Portuguese</span>
                        <span className="text-green-600 font-medium">
                          {knowledgeItems.filter(i => i.language === 'pt').length + intentResponses.filter(i => i.language === 'pt').length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* AI Teacher - Interactive Tutorial */}
      <AITeacher currentPage="trainer" />
    </div>
  );
};